/**
 * Sales Pipeline Intelligence Mycelium Connector
 *
 * Correlates activities across Gmail, Calendar, Tasks, and Opportunities
 * to calculate true deal velocity and engagement patterns.
 *
 * Key Features:
 * - Correlates emails, meetings, tasks by opportunity/client
 * - Calculates deal velocity metrics
 * - Detects engagement patterns (hot/warm/cold)
 * - Identifies deal risks and opportunities
 * - Publishes pipeline intelligence updates
 *
 * Flow Example:
 * Client sends email → Meeting scheduled → Task completed
 * → Connector correlates all activities
 * → Calculates engagement score
 * → Updates deal velocity
 * → Publishes pipeline_intelligence_updated
 *
 * @module mycelium-sales-pipeline-intelligence
 * @since 2026-02-20
 */

import { myceliumEventBackbone, type MyceliumEvent } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";
import { typedSupabase } from "../lib/typed-supabase";

// ============================================================================
// CONFIGURATION
// ============================================================================

const CONFIG = {
  correlationWindow: 24 * 60 * 60 * 1000, // 24 hours
  engagementThresholds: {
    hot: 10,    // 10+ activities in window
    warm: 5,    // 5-9 activities
    cold: 2,    // 2-4 activities
    inactive: 0, // 0-1 activities
  },
  velocityUpdateInterval: 3600000, // 1 hour
} as const;

// ============================================================================
// TYPES
// ============================================================================

interface DealActivity {
  opportunityId: string;
  clientId: string;
  activities: {
    emails: number;
    meetings: number;
    tasks: number;
    calls: number;
  };
  lastActivity: string;
  velocity: number;
  engagementLevel: 'hot' | 'warm' | 'cold' | 'inactive';
}

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class SalesPipelineIntelligenceConnector {
  private initialized = false;
  private dealActivityCache = new Map<string, DealActivity>();

  async initialize(): Promise<void> {
    if (this.initialized) return;

    logger.info("📊 Sales Pipeline Intelligence connector initializing");

    // Subscribe to multiple domains
    myceliumEventBackbone.subscribe({
      name: "SalesPipelineIntelligenceConnector",
      domains: ["integration", "calendar", "task", "sales"],
      handler: this.handleActivityEvent.bind(this),
      priority: 7,
    });

    this.initialized = true;
    logger.info("✅ Sales Pipeline Intelligence connector ready");
  }

  private async handleActivityEvent(event: MyceliumEvent): Promise<void> {
    const { type, payload } = event;

    try {
      // Extract client/opportunity context
      const clientId = payload.clientId as string || payload.client_id as string;
      const opportunityId = payload.opportunityId as string || payload.opportunity_id as string;

      if (!clientId && !opportunityId) return;

      // Update activity tracking
      await this.trackActivity(clientId, opportunityId, type);
    } catch (error) {
      logger.error("❌ SalesPipelineIntelligence handler error", {
        eventType: type,
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  private async trackActivity(
    clientId: string | undefined,
    opportunityId: string | undefined,
    activityType: string
  ): Promise<void> {
    if (!clientId || !opportunityId) return;

    const key = opportunityId;
    let dealActivity = this.dealActivityCache.get(key);

    if (!dealActivity) {
      dealActivity = {
        opportunityId,
        clientId,
        activities: { emails: 0, meetings: 0, tasks: 0, calls: 0 },
        lastActivity: new Date().toISOString(),
        velocity: 0,
        engagementLevel: 'inactive',
      };
    }

    // Increment activity counter
    if (activityType.includes('email') || activityType.includes('gmail')) {
      dealActivity.activities.emails++;
    } else if (activityType.includes('meeting') || activityType.includes('calendar')) {
      dealActivity.activities.meetings++;
    } else if (activityType.includes('task')) {
      dealActivity.activities.tasks++;
    } else if (activityType.includes('call')) {
      dealActivity.activities.calls++;
    }

    dealActivity.lastActivity = new Date().toISOString();

    // Calculate engagement level
    const totalActivities = Object.values(dealActivity.activities).reduce((a, b) => a + b, 0);
    if (totalActivities >= CONFIG.engagementThresholds.hot) {
      dealActivity.engagementLevel = 'hot';
    } else if (totalActivities >= CONFIG.engagementThresholds.warm) {
      dealActivity.engagementLevel = 'warm';
    } else if (totalActivities >= CONFIG.engagementThresholds.cold) {
      dealActivity.engagementLevel = 'cold';
    } else {
      dealActivity.engagementLevel = 'inactive';
    }

    // Calculate velocity
    dealActivity.velocity = totalActivities / 24; // Activities per day

    this.dealActivityCache.set(key, dealActivity);

    // Publish intelligence update
    await getMyceliumTransport().publish({
      domain: "sales",
      type: "pipeline_intelligence_updated",
      payload: {
        opportunityId,
        clientId,
        engagementLevel: dealActivity.engagementLevel,
        velocity: dealActivity.velocity,
        activities: dealActivity.activities,
      },
    });
  }

  async dispose(): Promise<void> {
    this.dealActivityCache.clear();
    this.initialized = false;
  }
}

export const salesPipelineIntelligenceConnector = new SalesPipelineIntelligenceConnector();
